import type { Column } from '@shared/types/data-table'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import type { FxSchedule } from '@features/fx-schedules/types/fx-schedule.types'
import { createIdColumn, createCustomColumn, createTextColumn, createDateColumn } from './column-factories'
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge'
import { FX_PROGRAM_OPTIONS } from '@features/fx-schedules/constants/fx-schedule-constants'

export const FX_SCHEDULE_COLUMNS: Record<string, Column<FxSchedule>> = {
  id: createIdColumn<FxSchedule>(),
  name: createTextColumn<FxSchedule>(
    'name',
    'Name',
    140,
    'text-sm font-medium',
  ),
  status: { ...createCustomColumn<FxSchedule>(
    'status',
    'Status',
    120,
    (schedule: FxSchedule) => (
      <AppStatusBadge entityType="fx-schedule" status={schedule.status || 'unknown'} />
    ),
  ), header: '' },
  description: createTextColumn<FxSchedule>(
    'description' as keyof FxSchedule,
    'Description',
    220,
    'text-sm text-muted-foreground',
  ),
  program: createCustomColumn<FxSchedule>(
    'program',
    'Program',
    140,
    (schedule: FxSchedule) => {
      const label = FX_PROGRAM_OPTIONS.find((p) => p.value === schedule.program)?.label || schedule.program
      return <span className="text-sm text-muted-foreground">{label}</span>
    },
  ),
  merchant: createCustomColumn<FxSchedule>(
    'merchant',
    'Merchant',
    180,
    (schedule: FxSchedule) => (
      <span className="text-sm text-muted-foreground">
        {schedule.merchant || '\u2014'}
      </span>
    ),
  ),
  createdDate: createDateColumn<FxSchedule>(
    'createdDate' as keyof FxSchedule,
    'Created',
    140,
    false,
  ),
  effectiveDate: createCustomColumn<FxSchedule>(
    'effectiveDate',
    'Effective',
    140,
    (schedule: FxSchedule) => (
      <span className="text-sm text-muted-foreground tabular-nums">
        {schedule.effectiveDate || (schedule.scheduledEffectiveDate ? `Scheduled: ${schedule.scheduledEffectiveDate}` : '\u2014')}
      </span>
    ),
  ),
  updatedDate: createDateColumn<FxSchedule>(
    'updatedDate' as keyof FxSchedule,
    'Updated',
    140,
    false,
  ),
}

const FX_SCHEDULE_COLUMN_ORDER = ['id', 'name', 'status', 'description', 'program', 'merchant', 'createdDate', 'effectiveDate', 'updatedDate'] as const

const FX_SCHEDULE_STATUS_CONFIG: Record<string, readonly string[]> = {
  all: FX_SCHEDULE_COLUMN_ORDER,
  active: FX_SCHEDULE_COLUMN_ORDER,
  draft: FX_SCHEDULE_COLUMN_ORDER,
  archived: FX_SCHEDULE_COLUMN_ORDER,
}

export const getFxScheduleColumns = (status: string = 'all'): Column<FxSchedule>[] => {
  const keys =
    FX_SCHEDULE_STATUS_CONFIG[status as keyof typeof FX_SCHEDULE_STATUS_CONFIG] ||
    FX_SCHEDULE_STATUS_CONFIG.all
  return keys.map((key) => FX_SCHEDULE_COLUMNS[key]).filter(Boolean)
}

export const getFxScheduleColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(FX_SCHEDULE_STATUS_CONFIG[status as keyof typeof FX_SCHEDULE_STATUS_CONFIG] || FX_SCHEDULE_STATUS_CONFIG.all)
  return Object.entries(FX_SCHEDULE_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['id', 'name', 'status'].includes(key),
    order: index,
  }))
}
